import React, { Component } from "react";
import "../App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Spinner, Card, Form } from "react-bootstrap";
import logo from "../assets/images/logo.png";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";

const baseUrl = process.env.REACT_APP_BASE_URL;

class Register extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Form step: 'register' or 'verify-otp'
      step: "register",
      showPassword: false,
      passwordFocus: false,
      loading: false,
      disabled: false,
      registerPhone: "",
      registerEmail: "",
      registerPassword: "",
      registerConfirmPassword: "",
      // OTP verification
      otpCode: "",
      otpSending: false,
      otpVerifying: false,
      resendCooldown: 0,
      // Inline validation
      errors: {
        email: null,
        phone: null,
        password: null,
        confirmPassword: null,
      },
      touched: {
        email: false,
        phone: false,
        password: false,
        confirmPassword: false,
      },
    };
    this.togglePassword = this.togglePassword.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.cooldownInterval = null;
  }

  componentWillUnmount() {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }

  togglePassword = (e) => {
    this.setState({ showPassword: e.target.checked });
  };

  handleFocus = () => {
    this.setState({ passwordFocus: true });
  };

  handleBlur = () => {
    this.setState({ passwordFocus: false });
  };

  // Inline validation helpers
  validateEmailField = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email || !email.trim()) {
      return "Email is required";
    }
    if (!emailRegex.test(email.trim().toLowerCase())) {
      return "Please enter a valid email address";
    }
    return null;
  };

  validatePhoneField = (phone) => {
    const cleanedPhone = phone.replace(/[\s-]/g, "");
    const phoneRegex = /^(\+?234|0)?[789][01]\d{8}$/;
    if (!cleanedPhone) {
      return "Phone number is required";
    }
    if (!phoneRegex.test(cleanedPhone)) {
      return "Please enter a valid Nigerian phone number";
    }
    return null;
  };

  validatePasswordField = (password) => {
    if (!password) {
      return "Password is required";
    }
    if (password.length < 6) {
      return "Password must be at least 6 characters";
    }
    if (!/[0-9]/.test(password)) {
      return "Must contain at least one number";
    }
    if (!/[a-z]/.test(password)) {
      return "Must contain at least one lowercase letter";
    }
    if (!/[A-Z]/.test(password)) {
      return "Must contain at least one uppercase letter";
    }
    if (!/[\W_]/.test(password)) {
      return "Must contain at least one special character";
    }
    return null;
  };

  validateConfirmPasswordField = (confirmPassword, password) => {
    if (!confirmPassword) {
      return "Please confirm your password";
    }
    if (confirmPassword !== password) {
      return "Passwords do not match";
    }
    return null;
  };

  handleFieldBlur = (field) => {
    const { registerEmail, registerPhone, registerPassword, registerConfirmPassword } = this.state;

    this.setState((prevState) => ({
      touched: { ...prevState.touched, [field]: true },
    }));

    let error = null;
    switch (field) {
      case "email":
        error = this.validateEmailField(registerEmail);
        break;
      case "phone":
        error = this.validatePhoneField(registerPhone);
        break;
      case "password":
        error = this.validatePasswordField(registerPassword);
        // Also revalidate confirm password if it has been touched
        if (this.state.touched.confirmPassword && registerConfirmPassword) {
          const confirmError = this.validateConfirmPasswordField(registerConfirmPassword, registerPassword);
          this.setState((prevState) => ({
            errors: { ...prevState.errors, confirmPassword: confirmError },
          }));
        }
        break;
      case "confirmPassword":
        error = this.validateConfirmPasswordField(registerConfirmPassword, registerPassword);
        break;
      default:
        break;
    }

    this.setState((prevState) => ({
      errors: { ...prevState.errors, [field]: error },
    }));
  };

  // Password strength calculator
  calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: "", color: "", percentage: 0 };

    let score = 0;

    // Length checks
    if (password.length >= 6) score += 1;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character type checks
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[\W_]/.test(password)) score += 1;

    let label, color;
    if (score <= 2) {
      label = "Weak";
      color = "#dc3545";
    } else if (score <= 4) {
      label = "Fair";
      color = "#ffc107";
    } else if (score <= 6) {
      label = "Good";
      color = "#17a2b8";
    } else {
      label = "Strong";
      color = "#28a745";
    }

    return { score, label, color, percentage: (score / 7) * 100 };
  };

  renderPasswordStrengthMeter = () => {
    const { registerPassword } = this.state;
    const strength = this.calculatePasswordStrength(registerPassword);

    if (!registerPassword) return null;

    return (
      <div className="mb-2 mt-1">
        <div
          style={{
            height: "4px",
            backgroundColor: "#e9ecef",
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${strength.percentage}%`,
              backgroundColor: strength.color,
              transition: "width 0.3s ease, background-color 0.3s ease",
            }}
          />
        </div>
        <small style={{ color: strength.color, fontWeight: "500" }}>
          Password strength: {strength.label}
        </small>
      </div>
    );
  };

  validateForm = () => {
    const {
      registerConfirmPassword,
      registerEmail,
      registerPassword,
      registerPhone,
    } = this.state;

    // Robust email validation pattern - requires valid TLD (at least 2 chars)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_]).{6,}$/;

    // Nigerian phone number patterns
    const cleanedPhone = registerPhone.replace(/[\s-]/g, "");
    const phoneRegex = /^(\+?234|0)?[789][01]\d{8}$/;

    if (!cleanedPhone || !phoneRegex.test(cleanedPhone)) {
      Swal.fire({
        title: "Invalid Phone Number",
        text: "Please enter a valid Nigerian phone number (e.g., 08012345678)",
        icon: "error",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!passwordRegex.test(registerPassword)) {
      Swal.fire({
        title: "Invalid Password",
        text: "Password must contain at least 1 number, 1 uppercase letter, 1 lowercase letter, and 1 special character.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (
      !registerEmail.trim() ||
      !emailRegex.test(registerEmail.trim().toLowerCase())
    ) {
      Swal.fire({
        title: "Invalid Email",
        text: "Please enter a valid email address (e.g., user@example.com)",
        icon: "error",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (registerPassword !== registerConfirmPassword) {
      Swal.fire({
        title: "Passwords Do Not Match",
        text: "The entered passwords do not match. Please make sure to enter the same password in both fields.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return false;
    }

    return true;
  };

  validateRegistrationDetails = async () => {
    const { registerEmail, registerPhone } = this.state;

    try {
      const url = `${baseUrl}/v2/registration/validate-details`;
      await axios.post(url, {
        email: registerEmail.trim().toLowerCase(),
        phone: registerPhone,
      });
      return true;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Failed to validate registration details. Please try again.";
      Swal.fire({
        title: "Registration Error",
        text: message,
        icon: "error",
        confirmButtonText: "OK",
      });
      return false;
    }
  };

  sendOtp = async () => {
    const { registerEmail } = this.state;

    this.setState({ otpSending: true });

    try {
      // First validate that email and phone are unique
      const isValid = await this.validateRegistrationDetails();
      if (!isValid) {
        this.setState({ otpSending: false });
        return;
      }

      const url = `${baseUrl}/v2/email-verification/send-otp`;
      await axios.post(url, { email: registerEmail.trim().toLowerCase() });

      // Start cooldown timer (60 seconds)
      this.setState({ resendCooldown: 60, step: "verify-otp" });
      this.cooldownInterval = setInterval(() => {
        this.setState((prevState) => {
          if (prevState.resendCooldown <= 1) {
            clearInterval(this.cooldownInterval);
            return { resendCooldown: 0 };
          }
          return { resendCooldown: prevState.resendCooldown - 1 };
        });
      }, 1000);

      Swal.fire({
        title: "Verification Code Sent",
        text: "A 6-digit verification code has been sent to your email. Please check your inbox (and spam folder). If it hasn't arrived after a few minutes, use 'Resend verification code'.",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "We couldn't send the verification code because of a problem on our side. Please try again in a few minutes, or contact info@nicfost.gov.ng if it keeps failing.";
      Swal.fire({
        title: "Error",
        text: message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      this.setState({ otpSending: false });
    }
  };

  verifyOtpAndRegister = async () => {
    const { registerEmail, otpCode } = this.state;

    if (!otpCode || otpCode.length !== 6) {
      Swal.fire({
        title: "Invalid Code",
        text: "Please enter the 6-digit verification code sent to your email.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    this.setState({ otpVerifying: true });

    try {
      // Verify OTP
      const verifyUrl = `${baseUrl}/v2/email-verification/verify-otp`;
      await axios.post(verifyUrl, {
        email: registerEmail.trim().toLowerCase(),
        otpCode: otpCode,
      });

      // OTP verified, proceed with registration
      await this.completeRegistration();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Invalid verification code. Please try again.";
      Swal.fire({
        title: "Verification Failed",
        text: message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      this.setState({ otpVerifying: false });
    }
  };

  completeRegistration = async () => {
    const { registerEmail, registerPassword, registerPhone, registerConfirmPassword } =
      this.state;

    const self = this;
    this.setState({ loading: true, disabled: true });

    const payload = {
      password: registerPassword,
      phone: registerPhone,
      email: registerEmail.trim().toLowerCase(),
      confirmPassword: registerConfirmPassword,
    };

    try {
      const url = `${baseUrl}/v2/registration/register/create-account-information`;
      const response = await axios.post(url, payload);
      const { status, data } = response;

      if ((data && status === 200) || (data && status === 201)) {
        sessionStorage.setItem("registration:ongoing", JSON.stringify(data));
        sessionStorage.setItem(
          "registration:userId",
          JSON.stringify(data.userId)
        );
      }

      if (data && status === 201) {
        Swal.fire({
          html: "Your registration is still in progress. Please proceed to complete your registration.",
          icon: "info",
          timerProgressBar: false,
          showConfirmButton: true,
          showCancelButton: false,
          showCloseButton: false,
          confirmButtonText: "Complete Registration",
          allowOutsideClick: false,
        }).then(() => {
          self.props.history.push("/register-food-scientist-v3");
        });
      } else if (data && status === 200) {
        Swal.fire({
          title: "Email Verified!",
          text: "Your email has been verified. Please continue with your registration.",
          icon: "success",
          confirmButtonText: "Continue",
        }).then(() => {
          self.props.history.push("/register-food-scientist-v3");
        });
      }
    } catch (error) {
      this.setState({ loading: false, disabled: false });

      const message =
        error?.response?.data?.message ||
        "Encountered an error while attempting to register.";
      Swal.fire({
        html: message,
        icon: "error",
        timerProgressBar: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonColor: "#ff7b7b",
      });
    }
  };

  handleRegisterClick = () => {
    if (this.validateForm()) {
      this.sendOtp();
    }
  };

  goBackToRegister = () => {
    this.setState({ step: "register", otpCode: "" });
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  };

  renderOtpVerificationStep() {
    const { registerEmail, otpCode, otpVerifying, otpSending, resendCooldown } =
      this.state;

    return (
      <div className="container" style={{ marginTop: 20 }}>
        <h6 className="nicfostText">Verify Your Email</h6>
        <p className="text-muted" style={{ fontSize: 14 }}>
          We've sent a 6-digit verification code to:
          <br />
          <strong>{registerEmail}</strong>
        </p>

        <Form>
          <Form.Floating className="mb-3">
            <input
              className="form-control shadow-none text-center"
              type="text"
              maxLength="6"
              placeholder="000000"
              value={otpCode}
              onChange={(e) =>
                this.setState({
                  otpCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                })
              }
              style={{ fontSize: 24, letterSpacing: 8 }}
            />
            <label>Enter 6-digit code</label>
          </Form.Floating>

          <div className="d-grid gap-2 mb-3">
            <button
              style={{ backgroundColor: "#003314" }}
              className="btn btn-success btn-lg"
              onClick={this.verifyOtpAndRegister}
              disabled={otpVerifying || otpCode.length !== 6}
              type="button"
            >
              {otpVerifying ? (
                <Spinner animation="border" variant="light" size="sm" />
              ) : (
                "Verify & Continue"
              )}
            </button>
          </div>

          <div className="text-center mb-3">
            <button
              className="btn btn-link"
              onClick={this.sendOtp}
              disabled={otpSending || resendCooldown > 0}
              type="button"
              style={{ color: resendCooldown > 0 ? "#999" : "#003314" }}
            >
              {otpSending ? (
                <Spinner animation="border" size="sm" />
              ) : resendCooldown > 0 ? (
                `Resend code in ${resendCooldown}s`
              ) : (
                "Resend verification code"
              )}
            </button>
          </div>

          <p className="text-center text-muted" style={{ fontSize: 12 }}>
            Didn't get the code? Check your spam folder, then use "Resend verification code". If it
            still doesn't arrive, contact info@nicfost.gov.ng.
          </p>

          <div className="text-center">
            <button
              className="btn btn-link text-muted"
              onClick={this.goBackToRegister}
              type="button"
            >
              ← Back to registration
            </button>
          </div>
        </Form>
      </div>
    );
  }

  renderRegistrationForm() {
    const { loading, passwordFocus, showPassword, disabled, otpSending, errors, touched } =
      this.state;

    return (
      <div className="container" style={{ marginTop: 20 }}>
        <h6 className="nicfostText">Sign Up</h6>

        <Form>
          <Form.Floating className="mb-3">
            <input
              className={`form-control shadow-none ${
                touched.email && errors.email ? "is-invalid" : touched.email && !errors.email ? "is-valid" : ""
              }`}
              type="email"
              onChange={(e) => this.setState({ registerEmail: e.target.value })}
              onBlur={() => this.handleFieldBlur("email")}
            />
            <label>Email Address</label>
            {touched.email && errors.email && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {errors.email}
              </div>
            )}
          </Form.Floating>
          <Form.Floating className="mb-3">
            <input
              className={`form-control shadow-none ${
                touched.phone && errors.phone ? "is-invalid" : touched.phone && !errors.phone ? "is-valid" : ""
              }`}
              type="tel"
              placeholder="e.g. 08012345678"
              onChange={(e) => this.setState({ registerPhone: e.target.value })}
              onBlur={() => this.handleFieldBlur("phone")}
            />
            <label>Nigerian Phone Number</label>
            {touched.phone && errors.phone && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {errors.phone}
              </div>
            )}
          </Form.Floating>
          <Form.Floating>
            <input
              className={`form-control shadow-none ${
                touched.password && errors.password ? "is-invalid" : touched.password && !errors.password ? "is-valid" : ""
              }`}
              type={showPassword ? "text" : "password"}
              id="password"
              onChange={(e) =>
                this.setState({ registerPassword: e.target.value })
              }
              onFocus={this.handleFocus}
              onBlur={() => {
                this.handleBlur();
                this.handleFieldBlur("password");
              }}
            />
            <label>Password</label>
          </Form.Floating>
          {this.renderPasswordStrengthMeter()}
          {touched.password && errors.password && (
            <div className="text-danger small mb-2">{errors.password}</div>
          )}
          {passwordFocus && !errors.password && (
            <small className="text-muted">
              Password must contain at least 1 number, 1 uppercase letter, 1
              lowercase letter, and 1 special character (!@#$%^*).
            </small>
          )}
          <Form.Floating>
            <input
              className={`form-control mt-3 shadow-none ${
                touched.confirmPassword && errors.confirmPassword
                  ? "is-invalid"
                  : touched.confirmPassword && !errors.confirmPassword
                  ? "is-valid"
                  : ""
              }`}
              type={showPassword ? "text" : "password"}
              id="confirmPassword"
              onChange={(e) =>
                this.setState({
                  registerConfirmPassword: e.target.value,
                })
              }
              onBlur={() => this.handleFieldBlur("confirmPassword")}
            />
            <label>Confirm Password</label>
            {touched.confirmPassword && errors.confirmPassword && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {errors.confirmPassword}
              </div>
            )}
          </Form.Floating>
          <br />
          <Form.Floating>
            <input
              className="shadow-none"
              type="checkbox"
              onChange={this.togglePassword}
            />
            <span>{showPassword ? "Hide password" : "Show password"}</span>
          </Form.Floating>

          <div className="d-grid gap-2 mb-3" style={{ marginTop: 30 }}>
            <button
              style={{ backgroundColor: "#003314" }}
              className="btn btn-success btn-lg"
              onClick={this.handleRegisterClick}
              disabled={disabled || loading || otpSending}
              type="button"
            >
              {loading || otpSending ? (
                <Spinner animation="border" variant="light" size="sm" />
              ) : (
                "Register"
              )}
            </button>
          </div>
        </Form>

        <div style={{ position: "relative", top: 5 }}>
          <h5 className="text-center text-dark mobile-text">
            Already have an account?{" "}
            <Link
              to="/login"
              style={{ textDecoration: "underline", color: "green" }}
            >
              sign in
            </Link>
          </h5>
        </div>
      </div>
    );
  }

  render() {
    const { step } = this.state;

    return (
      <div className="loginContainer">
        <div
          className="d-flex justify-content-center"
          style={{
            width: "100%",
            top: 0,
            bottom: 0,
          }}
        >
          <Card
            style={{ backgroundColor: "rgba(255,255,255,0.7)" }}
            className="d-flex justify-content-center cardContainer loginContainer-content"
          >
            <img
              src={logo}
              alt=""
              style={{
                width: "15%",
                position: "absolute",
                top: 18,
                alignSelf: "center",
              }}
            />
            <h5
              className="nicfostText"
              style={{
                position: "relative",
                fontWeight: "bold",
                top: 25,
                color: "#333",
                textAlign: "center",
              }}
            >
              NIGERIAN COUNCIL OF FOOD SCIENCE AND TECHNOLOGY
              <br />
              (NiCFoST)
            </h5>
            <div className="mb-3"></div>
            <a
              href="https://nicfost.gov.ng/registration-instructions/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", alignSelf: "center" }}
            >
              <h6
                style={{
                  textTransform: "capitalize",
                  textDecoration: "underline",
                  marginTop: 10,
                  color: "red",
                  fontSize: 11,
                }}
              >
                Read registration instructions here.
              </h6>
            </a>

            {step === "register"
              ? this.renderRegistrationForm()
              : this.renderOtpVerificationStep()}
          </Card>
        </div>
      </div>
    );
  }
}

export default Register;
